<script lang="ts" setup>
import { Button } from '~ds/components/ui/button';
import { Icon } from '~ds/components/ui/icon';
import { ProgressCircle } from '~ds/components/ui/progress-circle';
import { ICONS } from '~ds/lib/icons';
import KUIUnifiedLayoutPublicNav from '~kui/components/KUI/Unified/Layout/Public/KUIUnifiedLayoutPublicNav.vue';
import KUIUnifiedNavigationStat from '~kui/components/KUI/Unified/Navigation/KUIUnifiedNavigationStat.vue';

import type { IaNavItem } from '~/composables/unified/useIaNavigation';

import { useIaAudience } from '~/composables/unified/useIaAudience';
import { useIaNavigation } from '~/composables/unified/useIaNavigation';
import { COACH_ROUTE_INDEX } from '~/constants/routes/coach';
import { UNIFIED_ROUTE_INDEX } from '~/constants/routes/unified';

/**
 * The new information architecture's bar, signed in or out, so the two read the same. Signed in, the
 * pill holds the student's places with their clarity and coin pills beside it; signed out, it holds
 * the places a guest can read, with the sign-in beside it. On a phone either set is the menu. Which
 * set is the visitor's IA audience's; `logoOnly` keeps only the wordmark, for a page whose visitor
 * is part-way through signing in. `signInItem` is the guest's sign-in beside the places, from the
 * public layout.
 */
interface Props {
  logoOnly?: boolean;
  signInItem?: IaNavItem;
}

const props = withDefaults(defineProps<Props>(), {
  logoOnly: false,
  signInItem: undefined,
});

const { t } = useI18n();
const accessibilityStore = useAccessibilityStore();
const { logout } = useAuth();
const { formatNumber } = useLocaleFormatting();
const audience = await useIaAudience();
const {
  signedIn,
  navItems,
  clarityScore,
  coinBalance,
  avatarIllustration,
  avatarColor,
  clarityLink,
  coinsLink,
  claritySelected,
  coinsSelected,
} = useIaNavigation(audience, { signInItem: () => props.signInItem });

const clarityPercent = computed(() => formatNumber(clarityScore.value / 100, { style: 'percent' }));
</script>

<template>
  <KUIUnifiedLayoutPublicNav
    :nav-items="navItems"
    :home-link="signedIn ? COACH_ROUTE_INDEX : UNIFIED_ROUTE_INDEX"
    :logo-label="t('unified.home')"
    logo-illustration="anyway-logo"
    size="lg"
    :logo-only="logoOnly"
    :animations-enabled="accessibilityStore.animationsEnabled"
    data-testid="ia-navigation"
  >
    <template v-if="signedIn" #actions>
      <div class="flex items-center gap-2">
        <KUIUnifiedNavigationStat
          :to="clarityLink"
          :selected="claritySelected"
          :value="clarityPercent"
          :label="t('unified.nav.clarityStat')"
          data-testid="ia-navigation-clarity"
        >
          <template #media>
            <!-- The ring is a touch wider than the pill's 34px picture slot, as in the design, so the
                 avatar inside it sits clear of the ring's track rather than under it. -->
            <ProgressCircle
              :value="clarityScore"
              tone="purple"
              size="sm"
              class="shrink-0"
            >
              <KUICoreAvatar
                as="span"
                :overflow-hidden="false"
                :illustration="avatarIllustration"
                :color="avatarColor"
                class="size-6"
              />
            </ProgressCircle>
          </template>
        </KUIUnifiedNavigationStat>
        <KUIUnifiedNavigationStat
          v-if="coinBalance !== null"
          :to="coinsLink"
          :selected="coinsSelected"
          :value="coinBalance"
          :label="t('unified.nav.coinsStat')"
          tone="lime"
          data-testid="ia-navigation-coins"
        >
          <template #media>
            <NuxtImg
              src="/images/illustrations/coin.webp"
              alt=""
              width="68"
              height="68"
              class="size-full object-contain"
            />
          </template>
        </KUIUnifiedNavigationStat>
      </div>
    </template>
    <template v-if="signedIn" #drawer="{ close }">
      <div class="flex flex-wrap gap-2 px-4 pt-8">
        <KUIUnifiedNavigationStat
          :to="clarityLink"
          :selected="claritySelected"
          :value="clarityPercent"
          :label="t('unified.nav.clarityStatMenu')"
          size="lg"
          @click="close"
        >
          <template #media>
            <ProgressCircle
              :value="clarityScore"
              tone="purple"
              class="size-13"
            >
              <KUICoreAvatar
                as="span"
                :overflow-hidden="false"
                :illustration="avatarIllustration"
                :color="avatarColor"
                class="size-9"
              />
            </ProgressCircle>
          </template>
        </KUIUnifiedNavigationStat>
        <KUIUnifiedNavigationStat
          v-if="coinBalance !== null"
          :to="coinsLink"
          :selected="coinsSelected"
          :value="coinBalance"
          :label="t('unified.nav.coinsStat')"
          tone="lime"
          size="lg"
          @click="close"
        >
          <template #media>
            <NuxtImg
              src="/images/illustrations/coin.webp"
              alt=""
              width="104"
              height="104"
              class="size-full object-contain"
            />
          </template>
        </KUIUnifiedNavigationStat>
      </div>
      <div class="mt-8 border-t border-border px-4 pt-3">
        <Button
          variant="ghost"
          size="lg"
          class="w-full justify-start gap-3.5"
          data-testid="ia-navigation-logout"
          @click="logout()"
        >
          <Icon :name="ICONS.logout" size="lg" />
          {{ t('unified.nav.logOut') }}
        </Button>
      </div>
    </template>
  </KUIUnifiedLayoutPublicNav>
</template>
